import { AnyStateMachine } from 'xstate';
import { sendTelesalesEmail } from '~/services';
import { SendTelesalesEmailParams } from '~/services/send-telesales-email';
import { EnrollmentDataSource } from '~/typings/context';
import { SegmentEvent, sendToSegment } from '~/utils/tracking';
import { EnrollmentMatches } from '../enrollment-selection';
import { ContextSendMatch } from '../utils/types';
import { TelesalesFunnelMachine } from './types';

/*
  five emails that the "share with customer" button can send
  based on the current state / step in the funnel
*/

export type EmailKey = keyof typeof EmailDetails;

export const EmailDetails = {
  ResumeAtUtilityConnect: {
    salesPhase: 'needs-utility-information',
    enrollmentOption: undefined,
    segmentEvent: 'resume_signup_email_sent',
    step: 'enrollment',
  },
  ResumeAtAccountNumber: {
    salesPhase: 'needs-utility-information',
    enrollmentOption: 'partner_provided',
    segmentEvent: 'resume_signup_email_with_account_number_sent',
    step: 'enrollment',
  },
  ResumeAtBillUpload: {
    salesPhase: 'needs-utility-information',
    enrollmentOption: 'bill_capture',
    segmentEvent: 'resume_signup_email_with_bill_upload_sent',
    step: 'enrollment',
  },
  ResumeAtBilling: {
    salesPhase: 'needs-payment-information',
    enrollmentOption: undefined,
    segmentEvent: 'resume_signup_email_sent',
    step: 'billing',
  },
  ResumeAtTerms: {
    salesPhase: 'needs-terms-and-conditions',
    enrollmentOption: undefined,
    segmentEvent: 'resume_signup_email_sent',
    step: 'terms',
  },
} satisfies Record<
  string,
  {
    salesPhase: SendTelesalesEmailParams['salesPhase'];
    segmentEvent: SegmentEvent['type'];
    enrollmentOption: EnrollmentDataSource | undefined;
    step: 'enrollment' | 'billing' | 'terms';
  }
>;

/*
  used by the email callback internally to perform the proper API call (sending an email)
  and firing a segment event
*/

export const sendEmail = async ({ key, userId }: { key: EmailKey; userId: number }) => {
  const { salesPhase, segmentEvent, step, enrollmentOption } = EmailDetails[key];
  const result = await sendTelesalesEmail({ userId, salesPhase, enrollmentOption });
  sendToSegment({ type: segmentEvent, userId, step });
  return result;
};

/*
  used in <CustomerDetails/> component
  chooses the right email based on the current state / step in the funnel
*/

type PickEmailParams = {
  funnel: ContextSendMatch<TelesalesFunnelMachine>;
  micro?: ContextSendMatch<AnyStateMachine>;
};

export const pickEmail = ({ funnel, micro }: PickEmailParams): EmailKey => {
  if (funnel.matches('enrollment')) {
    const inMicroState = micro?.matches as EnrollmentMatches;
    if (inMicroState('utilityConnect')) return 'ResumeAtUtilityConnect';
    if (inMicroState('accountNumber')) return 'ResumeAtAccountNumber';
    if (inMicroState('billUpload')) return 'ResumeAtBillUpload';
  } else if (funnel.matches('billing')) {
    return 'ResumeAtBilling';
  }
  return 'ResumeAtTerms';
};
